import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { PlusCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { ProviderForm } from "@/components/providers/ProviderForm";
import { ProviderList } from "@/components/providers/ProviderList";

type ProviderFormData = {
  name: string;
  country: string;
  city: string;
  street: string;
  postcode: string;
  website?: string;
  phone_number: string;
  description?: string;
  is_active: boolean;
};

export default function PrintProviders() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: providers, isLoading } = useQuery({
    queryKey: ["print-providers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("print_service_providers")
        .select("*")
        .order("name");

      if (error) {
        console.error("Error fetching print providers:", error);
        throw error;
      }

      return data;
    },
  });

  const filteredProviders = providers?.filter((provider) =>
    provider.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const createProviderMutation = useMutation({
    mutationFn: async (data: ProviderFormData) => {
      const { error } = await supabase
        .from("print_service_providers")
        .insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["print-providers"] });
      toast({
        title: "Success",
        description: "Print provider has been created successfully.",
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create print provider: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateProviderMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ProviderFormData;
    }) => {
      const { error } = await supabase
        .from("print_service_providers")
        .update(data)
        .eq("provider_id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["print-providers"] });
      toast({
        title: "Success",
        description: "Print provider has been updated successfully.",
      });
      setIsDialogOpen(false);
      setSelectedProvider(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update print provider: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: ProviderFormData) => {
    if (selectedProvider) {
      updateProviderMutation.mutate({
        id: selectedProvider.provider_id,
        data,
      });
    } else {
      createProviderMutation.mutate(data);
    }
  };

  const handleEdit = (provider: any) => {
    setSelectedProvider(provider);
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setSelectedProvider(null);
    setIsDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Print Providers</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddNew}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add New Print Provider
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>
                  {selectedProvider ? 'Edit Provider' : 'Add New Provider'}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[calc(90vh-8rem)]">
                <div className="pr-4">
                  <ProviderForm
                    initialData={selectedProvider}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsDialogOpen(false)}
                  />
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search providers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {isLoading ? (
          <div>Loading providers...</div>
        ) : (
          <ProviderList
            providers={filteredProviders || []}
            onEdit={handleEdit}
          />
        )}
      </div>
    </AdminLayout>
  );
}
